import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  ChevronLeft,
  Building2,
  LogIn,
  MessageSquare,
  ClipboardCheck,
  Calendar,
  BarChart3,
  Shield,
  Users,
  ArrowRight,
  Hotel,
  Smartphone,
  Lock,
  ChevronDown,
  Star,
  Zap,
  Clock,
  FileText,
  CheckCircle,
  Layers,
  Menu,
  X,
  Play,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import LandingDemo from "./LandingDemo";

const LoginPage = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const featuresRef = useRef(null);

  // Login modal state
  const [loginOpen, setLoginOpen] = useState(false);
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [userHotels, setUserHotels] = useState([]);
  const [userName, setUserName] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const handleCredentials = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Inserisci email e password");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/pre-login`, { email, password });
      const { hotels, name } = response.data;
      setUserName(name);
      if (hotels.length === 0) {
        toast.error("Nessun hotel assegnato al tuo profilo");
      } else if (hotels.length === 1) {
        await handleLogin(hotels[0].slug);
      } else {
        setUserHotels(hotels);
        setStep("hotel-select");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (hotelSlug) => {
    setLoading(true);
    try {
      await login(email, password, hotelSlug);
      toast.success("Accesso effettuato!");
      navigate("/", { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore di accesso");
    } finally {
      setLoading(false);
    }
  };

  const openLoginModal = () => {
    setStep("credentials");
    setEmail("");
    setPassword("");
    setUserHotels([]);
    setUserName("");
    setLoginOpen(true);
    setMobileMenuOpen(false);
  };

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const FEATURES = [
    {
      icon: MessageSquare,
      title: "Chat di Reparto",
      desc: "Canali dedicati per ogni reparto: Front Office, Housekeeping, Cucina, Manutenzione e altri. Comunicazione istantanea e ordinata.",
      accent: "amber",
      span: "md:col-span-2",
    },
    {
      icon: ClipboardCheck,
      title: "Gestione Compiti",
      desc: "Traccia compiti per camera o parola chiave. Carry-forward automatico dei task aperti al giorno successivo.",
      accent: "emerald",
      span: "",
    },
    {
      icon: Star,
      title: "Conti Aperti",
      desc: "Griglia visiva dei conti camera. Chiusura automatica alla mezzanotte dopo il checkout.",
      accent: "amber",
      span: "",
    },
    {
      icon: Calendar,
      title: "Event Manager",
      desc: "Ordini di servizio strutturati per reparto. Conferme, note e allegati per matrimoni, conferenze e gala.",
      accent: "emerald",
      span: "md:col-span-2",
    },
    {
      icon: BarChart3,
      title: "Report Automatici",
      desc: "Report giornaliero via email a fine turno con riepilogo messaggi, compiti aperti e chiusi.",
      accent: "stone",
      span: "",
    },
    {
      icon: Hotel,
      title: "Multi-Struttura",
      desc: "Gestisci più hotel da un'unica piattaforma. Ogni struttura ha il suo ambiente completamente isolato.",
      accent: "amber",
      span: "",
    },
    {
      icon: Shield,
      title: "Sicurezza Enterprise",
      desc: "Isolamento dati per hotel, autenticazione JWT, controllo accessi per ruolo. Nessun dato condiviso tra strutture.",
      accent: "emerald",
      span: "md:col-span-2",
    },
  ];

  const ROLES = [
    {
      role: "Super Admin",
      desc: "Gestione globale di tutti gli hotel, utenti e configurazioni.",
      icon: Shield,
      color: "bg-stone-900 text-white",
    },
    {
      role: "Admin Struttura",
      desc: "Amministrazione locale: canali, utenti e report del proprio hotel.",
      icon: Building2,
      color: "bg-amber-500 text-white",
    },
    {
      role: "Direzione",
      desc: "Visibilità completa su tutti i canali e compiti della struttura.",
      icon: Users,
      color: "bg-emerald-600 text-white",
    },
    {
      role: "Staff",
      desc: "Accesso ai canali assegnati del proprio reparto e hotel.",
      icon: Smartphone,
      color: "bg-stone-600 text-white",
    },
  ];

  const accentClasses = {
    amber: {
      iconBg: "bg-amber-50 border-amber-200",
      iconText: "text-amber-600",
      border: "hover:border-amber-300",
    },
    emerald: {
      iconBg: "bg-emerald-50 border-emerald-200",
      iconText: "text-emerald-600",
      border: "hover:border-emerald-300",
    },
    stone: {
      iconBg: "bg-stone-100 border-stone-300",
      iconText: "text-stone-700",
      border: "hover:border-stone-400",
    },
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* ===== HEADER ===== */}
      <header className="fixed top-0 inset-x-0 z-50 bg-stone-900/95 backdrop-blur-md border-b border-stone-800" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-outfit text-xl font-bold text-white tracking-tight">
              End<span className="text-amber-400">•</span>ver
            </span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={scrollToFeatures} className="text-sm text-stone-300 hover:text-white transition-colors">
              Funzionalit&agrave;
            </button>
            <a href="#roles" className="text-sm text-stone-300 hover:text-white transition-colors">
              Ruoli
            </a>
            <a href="#security" className="text-sm text-stone-300 hover:text-white transition-colors">
              Sicurezza
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => setContactOpen(true)}
              variant="outline"
              className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 font-medium rounded-full px-5 h-9 text-sm hidden sm:flex"
              data-testid="nav-contact-button"
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" />
              Contattaci
            </Button>
            <Button
              onClick={openLoginModal}
              className="bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-full px-6 h-9 text-sm hidden sm:flex"
              data-testid="nav-login-button"
            >
              <LogIn className="w-4 h-4 mr-1.5" />
              Accedi
            </Button>
            {/* Mobile menu */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-stone-300 hover:text-white p-1"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-stone-900 border-t border-stone-800 px-4 pb-4 space-y-2">
            <button onClick={scrollToFeatures} className="block w-full text-left py-2 text-stone-300 hover:text-white text-sm">
              Funzionalità
            </button>
            <a href="#roles" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-stone-300 hover:text-white text-sm">
              Ruoli
            </a>
            <a href="#security" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-stone-300 hover:text-white text-sm">
              Sicurezza
            </a>
            <Button
              onClick={openLoginModal}
              className="w-full bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-full h-10 mt-2"
              data-testid="mobile-login-button"
            >
              <LogIn className="w-4 h-4 mr-1.5" />
              Accedi
            </Button>
          </div>
        )}
      </header>

      {/* ===== HERO + TUTORIAL ===== */}
      <section className="relative pt-16 overflow-hidden" data-testid="hero-section">
        <div className="bg-stone-900 relative">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              {/* Left: text + CTA */}
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
                  <Zap className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-xs font-medium text-amber-300 tracking-wide uppercase">
                    Piattaforma Operativa per Hotel
                  </span>
                </div>

                <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6">
                  Rivoluziona il{" "}
                  <span className="text-amber-400">Passaggio Consegne</span>{" "}
                  del tuo Hotel
                </h1>

                <p className="text-base sm:text-lg text-stone-400 leading-relaxed mb-8 max-w-xl">
                  Chat di reparto, gestione compiti per camera, conti aperti con chiusura automatica, 
                  eventi strutturati e report giornalieri. Tutto mobile-first, tutto in un unico strumento.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 mb-10">
                  <Button
                    onClick={openLoginModal}
                    className="bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-full px-8 h-12 text-base"
                    data-testid="hero-login-button"
                  >
                    Accedi al tuo Account
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    onClick={scrollToFeatures}
                    variant="outline"
                    className="border-stone-600 text-stone-300 hover:bg-stone-800 hover:text-white rounded-full px-8 h-12 text-base"
                    data-testid="hero-features-button"
                  >
                    Scopri le Funzionalità
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-stone-800">
                  {[
                    { value: "24/7", label: "Disponibilità" },
                    { value: "10+", label: "Reparti gestibili" },
                    { value: "100%", label: "Mobile-first" },
                    { value: "Multi", label: "Struttura" },
                  ].map((stat) => (
                    <div key={stat.label}>
                      <p className="font-outfit text-2xl sm:text-3xl font-bold text-amber-400">{stat.value}</p>
                      <p className="text-sm text-stone-500 mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: interactive demo */}
              <div className="relative" data-testid="demo-section">
                <div className="bg-stone-800/40 backdrop-blur-sm rounded-3xl p-5 sm:p-6 border border-stone-700/50">
                  <LandingDemo />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES BENTO ===== */}
      <section ref={featuresRef} id="features" className="py-16 sm:py-24 lg:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
              Tutto ciò di cui la tua Struttura ha bisogno
            </h2>
            <p className="text-base sm:text-lg text-stone-500 mt-4 max-w-2xl mx-auto">
              Dalla comunicazione interna alla gestione eventi, 
              End<span className="text-amber-500 font-semibold">•</span>ver copre ogni aspetto operativo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
            {FEATURES.map((f) => {
              const ac = accentClasses[f.accent];
              return (
                <div
                  key={f.title}
                  className={`group bg-white rounded-2xl p-6 border border-stone-200/80 shadow-sm ${ac.border} hover:shadow-md transition-all duration-300 ${f.span}`}
                  data-testid={`feature-card-${f.title.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className={`w-11 h-11 rounded-xl ${ac.iconBg} border flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <f.icon className={`w-5 h-5 ${ac.iconText}`} />
                  </div>
                  <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-16 sm:py-24 bg-stone-900" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Come Funziona
            </h2>
            <p className="text-base sm:text-lg text-stone-400 mt-4 max-w-2xl mx-auto">
              Tre semplici passaggi per digitalizzare il tuo passaggio consegne
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                icon: Users,
                title: "Il Super Admin crea gli account",
                desc: "L'amministratore configura hotel, reparti e crea gli account per ogni membro dello staff.",
              },
              {
                step: "02",
                icon: Smartphone,
                title: "Lo staff accede dal telefono",
                desc: "Ogni operatore accede con le proprie credenziali e seleziona la struttura. Interfaccia ottimizzata per mobile.",
              },
              {
                step: "03",
                icon: MessageSquare,
                title: "Comunicazione e compiti fluiscono",
                desc: "Messaggi, task, conti aperti e report vengono gestiti in tempo reale tra i reparti.",
              },
            ].map((item) => (
              <div key={item.step} className="relative">
                <div className="text-6xl font-outfit font-bold text-stone-800 absolute -top-2 right-4 select-none">
                  {item.step}
                </div>
                <div className="bg-stone-800/50 backdrop-blur-sm rounded-2xl p-6 border border-stone-700 relative">
                  <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-amber-400" />
                  </div>
                  <h3 className="font-outfit text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-stone-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ROLES ===== */}
      <section id="roles" className="py-16 sm:py-24 lg:py-32" data-testid="roles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
              Permessi e Ruoli Personalizzati
            </h2>
            <p className="text-base sm:text-lg text-stone-500 mt-4 max-w-2xl mx-auto">
              Ogni membro del team vede solo ciò che gli compete. Nessun dato condiviso tra strutture diverse.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ROLES.map((r) => (
              <div
                key={r.role}
                className="bg-white rounded-2xl overflow-hidden border border-stone-200/80 shadow-sm hover:shadow-md transition-all group"
                data-testid={`role-card-${r.role.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className={`${r.color} p-5 flex items-center gap-3`}>
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <r.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-outfit font-semibold text-lg">{r.role}</h3>
                </div>
                <div className="p-5">
                  <p className="text-sm text-stone-500 leading-relaxed">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECURITY ===== */}
      <section id="security" className="py-16 sm:py-24 bg-stone-100/60" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 mb-6">
                <Lock className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs font-medium text-emerald-700 tracking-wide uppercase">Sicurezza Enterprise</span>
              </div>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight mb-6">
                Isolamento Totale dei Dati
              </h2>
              <p className="text-base text-stone-600 leading-relaxed mb-8">
                Ogni hotel opera in un ambiente completamente separato. 
                Nessun membro dello staff può accedere ai dati di una struttura diversa dalla propria. 
                Gli account vengono creati esclusivamente dal Super Admin.
              </p>
              <div className="space-y-4">
                {[
                  "Autenticazione JWT con cookie httpOnly",
                  "Isolamento dati per struttura (tenant isolation)",
                  "Controllo accessi basato su ruolo",
                  "Nessun dato condiviso tra hotel",
                  "Validazione server-side su ogni richiesta",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-stone-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl border border-stone-200 shadow-lg p-6 space-y-4">
                {/* Mock tenant isolation diagram */}
                <div className="text-center mb-4">
                  <Layers className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                  <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Architettura Multi-Tenant</p>
                </div>
                {["Hotel Alfa", "Hotel Beta", "Hotel Gamma"].map((name, i) => (
                  <div key={name} className="flex items-center gap-3 p-3 rounded-xl bg-stone-50 border border-stone-200">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
                    }`}>
                      <Hotel className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-900">{name}</p>
                      <p className="text-xs text-stone-400">Ambiente isolato</p>
                    </div>
                    <Lock className="w-4 h-4 text-stone-300" />
                  </div>
                ))}
                <div className="pt-3 border-t border-stone-200 text-center">
                  <p className="text-xs text-stone-400">Ogni hotel ha canali, utenti e dati separati</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA FINALE ===== */}
      <section className="py-16 sm:py-24 bg-stone-900" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
            Pronto a Digitalizzare il tuo Hotel?
          </h2>
          <p className="text-base text-stone-400 max-w-xl mx-auto mb-8">
            Porta End<span className="text-amber-400 font-semibold">•</span>ver nella tua struttura o accedi se hai già un account.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setContactOpen(true)}
              className="bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-full px-10 h-12 text-base"
              data-testid="cta-contact-button"
            >
              <Mail className="w-4 h-4 mr-2" />
              Entra a far parte di End•ver
            </Button>
            <Button
              onClick={openLoginModal}
              variant="outline"
              className="border-stone-600 text-stone-300 hover:bg-stone-800 hover:text-white rounded-full px-10 h-12 text-base"
              data-testid="cta-login-button"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Accedi al tuo Account
            </Button>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-stone-950 border-t border-stone-800 py-10" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="font-outfit text-lg font-bold text-white tracking-tight">
                End<span className="text-amber-400">•</span>ver
              </span>
              <span className="text-xs text-stone-600">|</span>
              <span className="text-xs text-stone-500">Sistema Gestionale Operativo per l'Hotellerie</span>
            </div>
            <p className="text-xs text-stone-600">
              Account gestiti tramite Super Admin
            </p>
          </div>
        </div>
      </footer>

      {/* ===== LOGIN MODAL ===== */}
      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent className="sm:max-w-md" data-testid="login-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-center">
              {step === "hotel-select" ? (
                <>Seleziona Struttura</>
              ) : (
                <>
                  End<span className="text-amber-500">•</span>ver
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-center">
              {step === "hotel-select"
                ? `Ciao ${userName}, seleziona la struttura`
                : "Accedi con le credenziali fornite dal tuo amministratore"
              }
            </DialogDescription>
          </DialogHeader>

          {step === "hotel-select" ? (
            <div className="space-y-3 py-2">
              <button
                onClick={() => setStep("credentials")}
                className="flex items-center gap-1 text-stone-500 hover:text-stone-700 transition-colors text-sm"
                data-testid="back-to-login"
              >
                <ChevronLeft className="w-4 h-4" />
                Indietro
              </button>

              {userHotels.map((hotel) => (
                <button
                  key={hotel.slug}
                  onClick={() => handleLogin(hotel.slug)}
                  disabled={loading}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-stone-200 bg-white hover:border-amber-400 hover:shadow-md transition-all text-left group"
                  data-testid={`hotel-${hotel.slug}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                    <Building2 className="w-6 h-6 text-amber-600" />
                  </div>
                  <span className="font-outfit font-bold text-stone-900">{hotel.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <form onSubmit={handleCredentials} className="space-y-4 py-2">
              <div>
                <Label htmlFor="modal-email" className="text-stone-700">Email</Label>
                <Input
                  id="modal-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@email.com"
                  className="mt-1"
                  data-testid="login-email"
                  autoComplete="email"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="modal-password" className="text-stone-700">Password</Label>
                <Input
                  id="modal-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="La tua password"
                  className="mt-1"
                  data-testid="login-password"
                  autoComplete="current-password"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-stone-900 hover:bg-stone-800 h-12 rounded-xl font-medium"
                data-testid="login-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Accedi
                  </>
                )}
              </Button>
              <p className="text-xs text-stone-400 text-center pt-2">
                Non hai un account? Chiedi al tuo Super Admin di crearne uno.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== CONTACT MODAL ===== */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="sm:max-w-md" data-testid="contact-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-center">
              Entra a far parte di End<span className="text-amber-500">•</span>ver
            </DialogTitle>
            <DialogDescription className="text-center">
              Porta il passaggio consegne digitale nel tuo hotel
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-5">
            <p className="text-sm text-stone-600 text-center leading-relaxed">
              Vuoi attivare End•ver nella tua struttura? Contattaci per una demo personalizzata 
              o per ricevere maggiori informazioni.
            </p>

            <a
              href="mailto:ddonadio90@gmail.com?subject=Richiesta%20informazioni%20End%E2%80%A2ver"
              className="flex items-center gap-4 p-4 rounded-2xl border-2 border-stone-200 bg-white hover:border-amber-400 hover:shadow-md transition-all group"
              data-testid="contact-email-link"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                <Mail className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="font-outfit font-bold text-stone-900">Scrivici</p>
                <p className="text-sm text-stone-500">ddonadio90@gmail.com</p>
              </div>
            </a>

            <p className="text-xs text-stone-400 text-center">
              Ti risponderemo entro 24 ore
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
